import { Formik, Form, Field, ErrorMessage } from "formik";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/providers";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { registerSchema } from "@arrowise/shared";

export function RegisterForm() {
    const { register, error, clearError } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);

    // Clear errors when component mounts
    useEffect(() => {
        clearError();
    }, [clearError]);

    return (
        <Formik
            initialValues={{ firstName: "", lastName: "", email: "", password: "", confirmPassword: "" }}
            validationSchema={registerSchema}
            onSubmit={async (values, { setSubmitting: setFmSubmitting }) => {
                setSubmitting(true);
                clearError();
                try {
                    // include confirmPassword in payload so backend/schema validation succeeds
                    await register(values as any);
                    toast.success("Account created successfully! Please verify your email.");
                    // Navigate to app since user will be auto-logged in but need email verification
                    navigate('/app');
                } catch (error: any) {
                    // Show specific error message from the API
                    const errorMessage = error?.message || "Registration failed";
                    toast.error(errorMessage);
                } finally {
                    setSubmitting(false);
                    setFmSubmitting(false);
                }
            }}
        >
            {({ isSubmitting }) => (
                <Form className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-sm font-medium" htmlFor="firstName">First name</label>
                            <Field as={Input} id="firstName" name="firstName" autoComplete="given-name" />
                            <ErrorMessage name="firstName" component="div" className="text-xs text-destructive" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-medium" htmlFor="lastName">Last name</label>
                            <Field as={Input} id="lastName" name="lastName" autoComplete="family-name" />
                            <ErrorMessage name="lastName" component="div" className="text-xs text-destructive" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium" htmlFor="email">Email</label>
                        <Field as={Input} id="email" name="email" type="email" autoComplete="email" />
                        <ErrorMessage name="email" component="div" className="text-xs text-destructive" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium" htmlFor="password">Password</label>
                        <Field as={Input} id="password" name="password" type="password" autoComplete="new-password" />
                        <ErrorMessage name="password" component="div" className="text-xs text-destructive" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium" htmlFor="confirmPassword">Confirm password</label>
                        <Field as={Input} id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" />
                        <ErrorMessage name="confirmPassword" component="div" className="text-xs text-destructive" />
                    </div>
                    {error && <div className="text-xs text-destructive">{error}</div>}
                    <Button type="submit" disabled={isSubmitting || submitting} className="w-full">Create account</Button>
                    <p className="text-xs text-muted-foreground text-center">Already have an account? <Link to="/login" className="text-primary hover:underline">Login</Link></p>
                </Form>
            )}
        </Formik>
    );
}
